/*
 * BroadcastInFlightBanner — sticky panel on TP Trips showing every trip with an
 * active broadcast. Displays driver-count texted, opened, accepted, and how long
 * ago the broadcast fired. Auto-refresh every 8s.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Radio, Users, Eye, CheckCircle2 } from "lucide-react";
import { apiClient } from "../lib/api";

export default function BroadcastInFlightBanner({ onSelectTrip }) {
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/tpt/broadcasts/active");
      setRows(data.active || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  if (!rows.length) return null;
  return (
    <div className="mb-4 rounded-lg border border-blue-300 bg-blue-50 p-3" data-testid="broadcast-inflight-banner">
      <div className="flex items-center gap-2 mb-2">
        <Radio className="w-4 h-4 text-blue-600 animate-pulse" />
        <div className="text-sm font-semibold text-blue-900">
          {rows.length} broadcast{rows.length !== 1 ? "s" : ""} in flight — waiting for a driver to accept
        </div>
      </div>
      <div className="space-y-1">
        {rows.map((r) => (
          <button
            key={r.trip_id}
            onClick={() => onSelectTrip?.(r.trip_id)}
            className="w-full flex items-center gap-3 text-xs text-blue-900 px-2 py-1.5 rounded hover:bg-blue-100 text-left"
            data-testid={`broadcast-row-${r.trip_id}`}
          >
            <div className="font-mono text-blue-500 shrink-0">#{r.trip_id.slice(0, 8)}</div>
            <div className="font-semibold shrink-0">{r.rider_name}</div>
            <div className="truncate text-blue-700 flex-1">{r.pickup_address}</div>
            <div className="flex items-center gap-3 text-[11px] shrink-0">
              <span className="inline-flex items-center gap-1" title="Total drivers texted"><Users size={11} /> {r.total_texted}</span>
              <span className="inline-flex items-center gap-1" title="Drivers who opened the link"><Eye size={11} /> {r.counts.opened || 0}</span>
              <span className="inline-flex items-center gap-1" title="Accepted"><CheckCircle2 size={11} /> {r.counts.accepted || 0}</span>
              {r.age_min != null && (
                <span className={`font-mono ${r.age_min > 5 ? "text-red-600 font-semibold" : "text-blue-600"}`} title="Time since broadcast fired">
                  {r.age_min < 1 ? "just now" : `${r.age_min}m ago`}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
